import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './i18n/i18n'
import './index.css'
import App from './App'
import PageLoader from './components/PageLoader'
import { initWeb3Auth, isWeb3AuthEnabled } from './lib/web3auth'
import { isWeb3Mode } from './lib/authMode'
import { initSentry, Sentry } from './lib/sentry'
// #1230 beforeinstallprompt はロード直後に一度だけ飛ぶため、最速で listener を張る
import './lib/installPrompt'
// REQ-60 数値の入力欄の上でホイールを回しても値を変えない (返金額などが黙って変わるのを防ぐ)
import './lib/numberInputWheel'
// REQ-59 demo・preview では、デプロイがあったら前の版を出さずに新しい版で開く
import { holdUntilLatest, shouldHoldForLatest } from './lib/swAutoReload'

// #383: Sentry を最初に初期化。DSN 未設定なら no-op なので local 開発に影響しない。
initSentry()

// Pre-initialize Web3Auth only in Web3 mode (?web3=true)
const web3Param = new URLSearchParams(window.location.search).get('web3') === 'true'
if (isWeb3AuthEnabled() && (web3Param || isWeb3Mode())) {
  initWeb3Auth().catch((err) =>
    console.warn('[Web3Auth] Pre-init failed (will retry on login):', err)
  )
}

// #2140 別のページに埋め込まれて開いたとき (紹介ページの体験の 2 画面) は、画面の中のスクロールが
// 端に着いても**埋め込み元のページへ伝えない**。伝わると、お店やお客様の画面をスクロールしている
// つもりが体験ページごと動く。単体で開いたとき (ふだんのアプリ) は何も変えない
// (スマホの引っぱって更新などに触らない)。
if (window.self !== window.top) document.documentElement.style.overscrollBehavior = 'contain'

const root = createRoot(document.getElementById('root')!)
const renderApp = () => root.render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <h2>エラーが発生しました</h2>
          <p>申し訳ありません。問題を検知しました。再読み込みしてください。</p>
          <button
            onClick={() => {
              resetError()
              window.location.reload()
            }}
            style={{
              marginTop: 16,
              padding: '8px 24px',
              borderRadius: 8,
              background: '#1D3557',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            再読み込み
          </button>
        </div>
      )}
    >
      <BrowserRouter>
        {/* #1150: en/ja 以外のロケールは i18n が dynamic import するため、
            初回はロード完了まで useTranslation が suspend する。route 外の
            コンポーネント (DemoBanner 等) も受け止められるよう root に置く */}
        <Suspense fallback={<PageLoader />}>
          <App />
        </Suspense>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)

// REQ-59 demo・preview で前の版で開いたときは、新しい版があるかを確かめるまで読み込み中を出す
// (新しい版があれば、前の版は出さずに読み直す)。それ以外 (本番など) は今までどおりすぐ出す
if (shouldHoldForLatest()) {
  root.render(<PageLoader />)
  void holdUntilLatest().then(renderApp)
} else {
  renderApp()
}
